import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["title", "headingText", "Content", "defaultOpen", "referenceTabIndex", "open", "onOpenChange", "onClose", "onClick", "onMouseDown", "use", "placement", "autoPlacement", "delay", "delayOnHold", "animateOnToggle", "disabled", "role", "aria-live", "aria-label", "maxInlineSize", "zIndex", "tabIndex", "innerCssStyles", "target", "_bodyElement", "children"];
import { Children, cloneElement, useRef, useEffect, useContext, useState, useMemo } from 'react';
import { isFragment } from 'react-is';
import devLogger from 'react-utils/devLogger';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import { syntheticEvent } from '../../utils/SyntheticEvent/SyntheticEvent';
import { useControllable } from '../../utils/useControllable/useControllable';
import { FLOATING_UI_PLACEMENTS_MAP } from './placementConstants';
import { useFloating, offset, flip, shift, arrow, hide, autoUpdate, useHover, useFocus, useDismiss, useInteractions, safePolygon, FloatingArrow, useTransitionStyles } from '@floating-ui/react';
import FloatingPortalWrapper from '../Popover/FloatingPortalWrapper';
import styled, { useTheme } from 'foundations-theming/utils';
import { TooltipHeader } from './TooltipHeader';
import { ChildrenWrapper } from '../ChildrenWrapper/ChildrenWrapper';
import { findDOMNode } from '../../../utils/findDOMNode';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { POPOVER_ANIMATION_DURATION, TOOLTIP_DELAY, TOOLTIP_DELAY_ON_HOLD } from '../../../constants/times';
import { ModalTransitionContext } from '../Modal/ModalTransitionContext';
import { getDefaultNode } from '../../utils/RootNode';
import { JUPITER_LAYER } from '../../constants';
import { TooltipContent } from './TooltipContent';
import { WIDTH } from '../../../constants/nonTokenisedValues';
import { useRTEContext } from '../../context/RTEContext';
import { TooltipProvider } from './TooltipProvider';
import { getTooltipIconStyleOverrides } from './Tooltip.helpers';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const defaultDelay = parseInt(TOOLTIP_DELAY, 10);
const defaultDelayOnHold = parseInt(TOOLTIP_DELAY_ON_HOLD, 10);
const animationDuration = parseInt(POPOVER_ANIMATION_DURATION, 10);

/**
 * Global scope array of all handleOpenChange functions for tooltips.
 */
const allTooltipOpeners = [];
const StyledFloatingContainer = styled.div.withConfig({
  displayName: "Tooltip__StyledFloatingContainer"
})(({
  $floatingStyles,
  $zIndex
}) => {
  return Object.assign({}, $floatingStyles, {
    zIndex: $zIndex // The z-index needs to be in the root-level tooltop element
  });
});
const StyledTooltipContainer = styled.div.withConfig({
  displayName: "Tooltip__StyledTooltipContainer"
})(({
  $use,
  $cssStyles,
  theme
}) => {
  const isLongform = $use === 'longform';
  return Object.assign({
    position: 'relative',
    display: 'block',
    boxShadow: theme.boxShadow['400'],
    lineHeight: '1.5',
    // This doesn't need to be tokenized because it's relatvie to the font size
    paddingInline: $use === 'longform' ? theme.space['500'] : theme.space['400'],
    paddingBlock: $use === 'longform' ? theme.space['500'] : theme.space['250'],
    textDecoration: 'none',
    wordWrap: 'break-word',
    fontSize: theme.typography['detail-200'].fontSize,
    borderRadius: theme.borderRadius['100'],
    backgroundColor: isLongform ? theme.color['fill-surface-default'] : $use === 'danger' ? theme.color['fill-alert-default'] : theme.color['fill-accent-neutral-default'],
    borderWidth: isLongform ? theme.borderWidth['100'] : undefined,
    borderStyle: isLongform ? 'solid' : undefined,
    borderColor: isLongform ? theme.color['border-transitional-core-subtle'] : undefined,
    color: isLongform ? undefined : theme.color['text-core-on-fill-default']
  }, isLongform ? {
    '& ul, & ol': {
      paddingInlineStart: theme.space['400']
    }
  } : {
    // Just target links and transparent/link buttons to avoid affecting other button styles
    '& a, & button[data-button-use^="link"], & button[data-button-use="transparent"], & button[data-button-use="unstyled"]': {
      color: theme.color['icon-interactive-on-fill-default'],
      textDecoration: 'underline',
      textUnderlineOffset: theme.typography['link'].textUnderlineOffset,
      fontWeight: theme.typography['body-125'].fontWeight,
      '&:hover': {
        color: theme.color['text-interactive-on-fill-hover']
      },
      '&:active': {
        color: theme.color['text-interactive-on-fill-pressed']
      }
    },
    '& svg[data-icon-name]': {
      fill: theme.color['icon-interactive-on-fill-default']
    },
    '& svg[data-icon-name="ExternalLink"]': {
      fill: `${theme.color['icon-interactive-on-fill-default']} !important`
    }
  }, $cssStyles);
});
export const Tooltip = _ref => {
  var _middlewareData$hide2;
  let {
      title,
      headingText,
      Content,
      defaultOpen = false,
      referenceTabIndex = 0,
      open,
      onOpenChange,
      onClose,
      onClick,
      onMouseDown,
      use = 'default',
      placement = 'top',
      autoPlacement = true,
      delay = defaultDelay,
      delayOnHold = defaultDelayOnHold,
      animateOnToggle = true,
      disabled = false,
      role = 'tooltip',
      'aria-live': ariaLive,
      'aria-label': ariaLabel,
      maxInlineSize = use === 'longform' ? '280px' : '232px',
      zIndex = parseInt(JUPITER_LAYER, 10),
      tabIndex,
      innerCssStyles: innerTokenProps = {},
      target,
      _bodyElement,
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [controllableOpen, handleOpenChange] = useControllable(open, defaultOpen, onOpenChange);
  // This is necessary to avoid duplicating tooltips inside Tabs (https://github.com/HubSpotEngineering/foundations-components/issues/3964)
  const {
    transitioning: inModalTransition
  } = useContext(ModalTransitionContext);
  const computedDisabled = disabled || Content == null && !title;
  const computedOpen = controllableOpen && !computedDisabled && !inModalTransition;

  // This state is set to `true` once the open animation completes
  const [isOpenComplete, setIsOpenComplete] = useState(false);
  const {
    domProps,
    tokenProps
  } = filterProps(Object.assign({}, rest, {
    maxInlineSize
  }));
  const outerCssStyles = useMapTokenPropsToCSS(tokenProps);
  const theme = useTheme();
  const rteContext = useRTEContext();
  const arrowRef = useRef(null);
  const {
    refs,
    floatingStyles,
    context,
    middlewareData
  } = useFloating({
    placement: FLOATING_UI_PLACEMENTS_MAP[placement],
    open: computedOpen,
    onOpenChange: newIsOpen => {
      if (!computedDisabled) {
        handleOpenChange(syntheticEvent(newIsOpen));

        // Tell all other tooltips on the page to close
        if (newIsOpen) {
          allTooltipOpeners.forEach(handler => {
            if (handler === handleOpenChange) return;
            handler(syntheticEvent(false));
          });
        }
        if (!newIsOpen && onClose) {
          onClose();
        }
      }
    },
    middleware: [offset(parseInt(theme.space['300'], 10)), autoPlacement ? flip() : undefined, autoPlacement ? shift() : undefined, arrow({
      element: arrowRef,
      padding: parseInt(theme.space['100'], 10)
    }), hide()],
    whileElementsMounted: autoUpdate,
    elements: {
      reference: target // The reference element defaults to the child unless the target prop is used
    }
  });

  // All interactions are disabled if the target prop is used
  const hover = useHover(context, {
    delay: {
      open: delay,
      close: 0
    },
    restMs: delayOnHold,
    handleClose: safePolygon(),
    enabled: !target
  });
  const focus = useFocus(context, {
    enabled: !target
  });
  const dismiss = useDismiss(context, {
    enabled: !target
  });
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([hover, focus, dismiss]);
  const tooltipId = useUniqueId('tooltip-');
  if (process.env.NODE_ENV !== 'production') {
    const child = children ? Children.only(children) : null;
    if (isFragment(child)) {
      devLogger.warn({
        message: `Tooltip: child element cannot be a React.Fragment, and must support passing event handlers through to a DOM element.`,
        key: 'Tooltip: fragment not supported'
      });
    }
  }
  const {
    isMounted,
    styles: animationStyles
  } = useTransitionStyles(context, {
    duration: animateOnToggle ? animationDuration : 0,
    common: ({
      side
    }) => ({
      transformOrigin: {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left'
      }[side]
    }),
    initial: {
      opacity: 0,
      transform: 'scale(0.75)'
    },
    open: {
      opacity: 1,
      transform: 'scale(1)'
    },
    close: {
      opacity: 0,
      transform: 'scale(0.75)'
    }
  });

  /**
   * Updates the reference element when the target prop changes.
   */
  useEffect(() => {
    if (target) {
      refs.setPositionReference(target);
    }
  }, [target, refs]);

  // Closes the tooltip when the reference element is hidden
  // jsdom doesn't have a layout engine, so we can't support this feature in tests
  useEffect(() => {
    var _middlewareData$hide;
    if (computedOpen && (_middlewareData$hide = middlewareData.hide) !== null && _middlewareData$hide !== void 0 && _middlewareData$hide.referenceHidden) {
      if (process.env.NODE_ENV === 'production') {
        handleOpenChange(syntheticEvent(false));
      }
    }
  }, [computedOpen, (_middlewareData$hide2 = middlewareData.hide) === null || _middlewareData$hide2 === void 0 ? void 0 : _middlewareData$hide2.referenceHidden, handleOpenChange]);

  // Sets the open complete state to false when the floating element is unmounted
  useEffect(() => {
    if (!isMounted) {
      setIsOpenComplete(false);
    }
  }, [isMounted]);

  // Registers the handleOpenChange of this current instance of tooltip to the global array
  // Runs ONLY on mount and unmount since handleOpenChange is a stable reference
  useEffect(() => {
    allTooltipOpeners.push(handleOpenChange);
    return () => {
      const index = allTooltipOpeners.indexOf(handleOpenChange);
      if (index > -1) {
        allTooltipOpeners.splice(index, 1);
      }
    };
  }, [handleOpenChange]);
  const clonedChild = useMemo(() => {
    const child = children ? Children.only(children) : null;
    if (!child) return null;
    if (computedDisabled) return child;
    const typedChild = child;
    const childProps = Object.assign({}, typedChild.props, {
      tabIndex: referenceTabIndex,
      // UIComponents#4949
      'aria-describedby': computedOpen ? `${typedChild.props['aria-describedby'] || ''} ${tooltipId}`.trim() : typedChild.props['aria-describedby']
    });
    if (target) {
      return /*#__PURE__*/cloneElement(child, childProps);
    }
    return /*#__PURE__*/cloneElement(child, getReferenceProps(childProps));
  }, [children, computedDisabled, referenceTabIndex, computedOpen, tooltipId, target, getReferenceProps]);
  const iconStyleOverrides = getTooltipIconStyleOverrides(use, theme);

  /**
   * "UIPopover" is intentional to provide backwards compatibility with Selenium helpers
   * @see https://github.com/HubSpotEngineering/foundations-components/issues/3770
   * @see https://github.com/HubSpotEngineering/hs-selenium/blob/e5e9a447c1c57503507c1ae0fe7ae982c7923f9a/selenium-js-utils/src/UIComponents/popover/internal/core.ts#L14
   */
  const componentName = 'UIPopover';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ChildrenWrapper
    // @ts-expect-error - Parameter 'childNode' implicitly has an 'any' type.
    , {
      ref: childNode => {
        if (!target) {
          /* eslint-disable-next-line react/no-find-dom-node */
          refs.setReference(findDOMNode(childNode));
        }
      },
      children: clonedChild
    }), isMounted && /*#__PURE__*/_jsx(FloatingPortalWrapper, {
      // Needed for AnimatePresence (see https://www.framer.com/motion/animate-presence/##exit-animations)
      root: _bodyElement || getDefaultNode(),
      children: /*#__PURE__*/_jsx(StyledFloatingContainer, Object.assign({}, getFloatingProps(Object.assign({}, domProps, {
        role,
        // Keeps tooltip open when clicked by making it focusable (matches UITooltip)
        tabIndex: tabIndex !== null && tabIndex !== void 0 ? tabIndex : -1,
        onClick,
        onMouseDown,
        id: tooltipId,
        'aria-live': ariaLive,
        'aria-label': ariaLabel
      })), {
        ref: refs.setFloating
        // Necessary for TinyMCE to prevent blur on the editor when component is clicked
        // https://github.com/HubSpotEngineering/structured-content/issues/702
      }, rteContext, {
        $floatingStyles: floatingStyles,
        $zIndex: zIndex,
        children: /*#__PURE__*/_jsx("div", {
          style: Object.assign({}, animationStyles),
          onTransitionEnd: () => {
            if (isMounted) {
              setIsOpenComplete(true);
            }
          },
          children: /*#__PURE__*/_jsxs(StyledTooltipContainer, {
            $use: use,
            $cssStyles: outerCssStyles,
            "data-component-name": componentName,
            "data-open-complete": isOpenComplete,
            children: [/*#__PURE__*/_jsx(TooltipProvider, {
              iconStyleOverrides: iconStyleOverrides,
              tooltipUse: use,
              children: Content == null ? /*#__PURE__*/_jsxs(TooltipContent, Object.assign({
                _use: use
              }, innerTokenProps, {
                children: [headingText && /*#__PURE__*/_jsx(TooltipHeader, {
                  use: use,
                  children: headingText
                }), title]
              })) : /*#__PURE__*/_jsx(Content, {})
            }), /*#__PURE__*/_jsx(FloatingArrow, {
              ref: arrowRef,
              context: context,
              fill: use === 'longform' ? theme.color['fill-surface-default'] : use === 'danger' ? theme.color['fill-accent-red-default'] : theme.color['fill-accent-neutral-default'],
              width: parseInt(WIDTH.TOOLTIP_FLOATING_ARROW, 10),
              height: 10,
              stroke: use === 'longform' ? theme.color['border-transitional-core-subtle'] : undefined,
              strokeWidth: use === 'longform' ? parseInt(theme.borderWidth['100'], 10) : undefined,
              tipRadius: use === 'longform' ? undefined : 1
            })]
          })
        })
      }))
    }, "tooltip-floating-portal")]
  });
};
Tooltip.displayName = 'Tooltip';